import {
  type CallhomeCamera,
  type CallhomeFloorplan,
  type CameraLinkId,
  type DeviceEntity,
  type SiteId,
  SiteStatus,
} from '@solinkcorp/api-types';
import { userIsLocalPlaybackOnly, type UserSession } from '@solinkcorp/authentication';
import { type LocaleDefinition } from '@solinkcorp/locale';
import settings from '@solinkcorp/settings';
import { ALL_LOCATIONS } from '@solinkcorp/vd-common';
import { type CallhomeCameraLink } from 'clients/CallhomeApiTypes';
import { AppDispatcher } from 'dispatcher';
import clone from 'lodash/clone';
import cloneDeep from 'lodash/cloneDeep';
import concat from 'lodash/concat';
import findIndex from 'lodash/findIndex';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import keyBy from 'lodash/keyBy';
import merge from 'lodash/merge';
import omit from 'lodash/omit';
import set from 'lodash/set';
import { isKiosk } from 'src/core/utils/kioskUtils';
import StorageAPI from 'src/core/utils/StorageAPI';
import * as CameraUtil from 'utils/CameraUtil';
import CommonUtil from 'utils/CommonUtil';
import LocaleUtil from 'utils/LocaleUtil';
import {
  type CamerasDictionary,
  type CamerasDictionaryCamera,
  type VideoDiscoverySite,
} from 'utils/types';
import * as constants from 'vd-constants';
import CameraActions from '../actions/CameraActions';
import { AuthStore } from './AuthStore';
import { BaseStore } from './BaseStore';
import CameraStore from './CameraStore';
import { SAVE_LOCATIONS_IN_DB } from './LocationWorker';
import { startWorker } from './LocationWorkerClient';
import { getReduxStore } from './store';

// This worker is restarted when user refresh the app and locations are pulled from API.
// It is closed when user close the app (browser window)
let workerClient: Worker | undefined;

// If the locations has been fetched from API
let _hasFetchedLocations = false;
// If the locations has been processed and ready to be used
// The locations can come from API or from indexedDB
let _isLocationsReady = false;

let _locations: VideoDiscoverySite[] = [];
let _locationsById: { [locationId: SiteId]: VideoDiscoverySite } | undefined;
let _error: string | null = null;
let _restrictions: {
  cameraIds?: string[];
  deviceIds?: string[];
} = {};
let _allLocationCamerasDict: CamerasDictionary = {};
let _hasNoPhysicalLocations = false;
const _floorplanByLocationId: { [locationId: string]: CallhomeFloorplan } = {};

// Unique device tags extracted from all available locations for the user
let _deviceTagIds: Set<string> | undefined;

let currentIP: string | undefined;

interface UpdateCameraLinkActionType {
  locationId: string;
  cameraLinkId: CameraLinkId;
  prop: string; //keyof CallhomeCameraLink;
  value: {
    isEnabled?: boolean;
    center?: {
      x: number;
      y: number;
    };
  };
}

interface InsertCameraLinkActionType {
  locationId: string;
  newCameraLink: CallhomeCameraLink;
}
/**
 * Update a location in _locations and _locationsById.
 *
 * This function will cloneDeep the location, and replace _locationsById, ensuring
 * that locations remain immutable.
 */
function updateLocation(locationId: string, fn: (location: VideoDiscoverySite) => void): void {
  const locationIndex = _locations.findIndex((location) => location.id === locationId);

  if (locationIndex > -1) {
    const newLocation = cloneDeep(_locations[locationIndex]);
    fn(newLocation);

    // If the new object is the same as the old one, don't update anything.
    if (!isEqual(_locations[locationIndex], newLocation)) {
      // Refresh embedded camera.location references when cameras or camera links change.
      if (
        !isEqual(_locations[locationIndex].cameras, newLocation.cameras) ||
        !isEqual(_locations[locationIndex].cameraLinks, newLocation.cameraLinks)
      ) {
        updateAllLocationCamerasDict(newLocation);
      }
      // Update locations and locations map
      _locations[locationIndex] = newLocation;
      _locationsById = keyBy(_locations, (location) => location.id);

      LocationStore.emitChange();
    }
  }
}

//
// obtain client ip
//
export function initCurrentIp() {
  Promise.resolve()
    .then(async () => {
      const response = await fetch(`${settings.auth_solink}/whatismyip`, { mode: 'cors' });
      const data = await response.json();
      currentIP = get(data, 'ip');
      console.info('Current client IP >>> ', currentIP);
    })
    .catch((err) => {
      console.error('Error checking IP address: ', err);
    });
}

_initializeStore();

function _initializeStore() {
  _locations = [];
  _locationsById = undefined;
  _error = null;
  _restrictions = {};
  _isLocationsReady = false;
  _hasFetchedLocations = false;
  _hasNoPhysicalLocations = false;
}

function sortNameAlphabetically(object1: CallhomeCamera, object2: CallhomeCamera) {
  // Sort objects alphabetically according to name property (note that e.g. "Store 12" would be placed before "Store 2")
  return CommonUtil.sortObjectAlphabetically(object1, object2, 'name');
}

function generateAllLocations(locale?: LocaleDefinition) {
  const localeToUse =
    locale || LocaleUtil.getLocale(getReduxStore().getState().localeState.localeCode);
  return {
    id: ALL_LOCATIONS,
    name: `${localeToUse.locationSelector.allLocations}`,
    alarmConfig: {},
  } as VideoDiscoverySite;
}

function setErrorState(msg: string | null) {
  _error = msg;
}

const getLocationsWithAllLocations = (locations: VideoDiscoverySite[]) =>
  locations.filter((l) => l.id !== ALL_LOCATIONS);

const getPhysicalLocations = (locations: VideoDiscoverySite[]) =>
  locations.filter(({ id, status }) => id !== ALL_LOCATIONS && status !== SiteStatus.VIRTUAL);

export const LocationStore = {
  ...BaseStore,
  getLocations(withoutAllLocations = false) {
    return withoutAllLocations ? getLocationsWithAllLocations(_locations) : _locations;
  },

  getPhysicalLocations() {
    return getPhysicalLocations(_locations);
  },

  getLocationsById() {
    return _locationsById;
  },

  getError() {
    return _error;
  },

  getLocation(id: SiteId): VideoDiscoverySite | undefined {
    return _locationsById?.[id] || undefined;
  },

  getAllLocationsOption(locale: LocaleDefinition) {
    return generateAllLocations(locale);
  },

  getRestrictedCameras() {
    return _restrictions.cameraIds;
  },

  getRestrictedLocations() {
    return _restrictions.deviceIds;
  },

  getRestrictions() {
    return _restrictions;
  },

  getAllLocationCamerasDict() {
    return _allLocationCamerasDict;
  },

  getIsLocationsReady() {
    return _isLocationsReady;
  },

  getHasFetchedLocations() {
    return _hasFetchedLocations;
  },

  getHasNoPhysicalLocations() {
    return _hasNoPhysicalLocations;
  },

  getFloorplanByLocationId() {
    return _floorplanByLocationId;
  },

  getAppliedDeviceTags() {
    return _deviceTagIds && Array.from(_deviceTagIds);
  },

  dispatchToken: AppDispatcher.register((action: any) => {
    switch (action.type) {
      case constants.GET_LOCATION_LIST: {
        // TODO: We should schedule updates for locations in the location store
        // based on their next health check.  Right now if a location's next health
        // check is 4 minutes in the future, and you wait 5 minutes, and then hover
        // over the location in the "Video" page, it will show as
        // "Missed health check by 1 minutes ago", which is not so good
        // (also, not terribly good english).
        const { results: locations = [], user, shouldCache, dbId } = action.payload;

        processLocationList(user, locations).then(() => {
          if (shouldCache) {
            _hasFetchedLocations = true;

            // save the processed locations in indexedDB with web worker (off the main thread)
            // the bulk put can be expensive when customer has a lot of locations
            if (!workerClient) {
              workerClient = startWorker();
            }
            workerClient.postMessage({
              taskType: SAVE_LOCATIONS_IN_DB,
              locations: _locations,
              dbId,
            });
          }
          _isLocationsReady = true;
          _hasNoPhysicalLocations = getPhysicalLocations(_locations).length === 0;
          CameraActions.mapCameraGroups();
          setErrorState(null);
          LocationStore.emitChange();
        });

        break;
      }

      case constants.CAMERA_NAME_UPDATED_SUCCESS:
      case constants.CAMERA_MOTION_UPDATED_SUCCESS:
      case constants.CAMERA_AI_UPDATED_SUCCESS: {
        const { deviceId, cameraId, name, ai, motionParams } = action.payload;

        updateLocation(deviceId, (location) => {
          const camera = location.cameras.find((cam) => cam.cameraId === cameraId);
          if (camera) {
            if (name) {
              camera.name = name;
              location.cameras.sort(sortNameAlphabetically);
            }
            if (motionParams) {
              camera.motionParams = motionParams;
            }
            if (ai) {
              camera.ai = ai;
            }
          }
        });

        setErrorState(null);
        LocationStore.emitChange();
        break;
      }

      case constants.CAMERA_NAME_UPDATED_FAILURE: {
        setErrorState(action.msg);
        LocationStore.emitChange();
        break;
      }

      case constants.CAMERA_ROI_UPDATED_SUCCESS: {
        const { uniqueCameraId, regions, locationId } = action.payload;
        updateLocation(locationId, (location) => {
          const camera = location.cameras.find((cam) => cam.id === uniqueCameraId);
          if (camera) {
            camera.regions = [...regions];
          }
        });
        LocationStore.emitChange();
        break;
      }

      case constants.CAMERA_TAGS_UPDATED_SUCCESS: {
        const { cameraId, locationId, tags } = action.payload;
        updateLocation(locationId, (location) => {
          const camera = location.cameras.find((cam) => cam.id === cameraId);
          if (camera) {
            camera.cameraTagIds = [...tags];
          }
        });
        LocationStore.emitChange();
        break;
      }

      case constants.CAMERA_PTZ_ENABLED_UPDATED_SUCCESS: {
        const { uniqueId, locationId, isPtzEnabled } = action.payload;
        updateLocation(locationId, (location) => {
          const camera = location.cameras.find((cam) => cam.id === uniqueId) as
            (CamerasDictionaryCamera & { isPtzEnabled?: boolean }) | undefined;
          if (camera) {
            camera.isPtzEnabled = isPtzEnabled;
          }
        });
        LocationStore.emitChange();
        break;
      }

      case constants.UPDATE_LOCALE: {
        if (!isEmpty(_locations)) {
          const { payload: localeCode } = action;
          const locale = LocaleUtil.getLocale(localeCode);
          if (_locations[0].id === ALL_LOCATIONS) {
            _locations = [generateAllLocations(locale), ..._locations.slice(1)];
          }

          const lastIdx = _locations.length - 1;
          if (_locations[lastIdx].id === constants.LOAD_MORE) {
            _locations = [
              ..._locations.slice(0, lastIdx),
              { ..._locations[lastIdx], name: locale.locationSelector.loadMore },
            ];
          }
        }

        LocationStore.emitChange();

        break;
      }

      case constants.USER_ROLE_CAMERA_RESTRICTIONS: {
        const cameraIds: string[] = [];
        const deviceIds: { [deviceId: string]: null } = {};

        forEach(action.cameras, (camera) => {
          cameraIds.push(camera.cameraId);
          deviceIds[camera.deviceId] = null;
        });

        _restrictions.cameraIds = cameraIds;
        _restrictions.deviceIds = Object.keys(deviceIds);

        LocationStore.emitChange();

        break;
      }

      case constants.UPDATE_CAMERA_ANNOTATION: {
        const { id, annotations } = action;

        let targetCamera;
        const locationId = _allLocationCamerasDict[id]?.deviceId;

        if (locationId) {
          updateLocation(locationId, (location) => {
            targetCamera = location.cameras.find((cam) => cam.id === id);
            if (targetCamera) {
              targetCamera.annotations = concat(annotations, []);
            }
          });
        }

        break;
      }

      case constants.UPDATE_CAMERA_LINK: {
        const { locationId, cameraLinkId, prop, value }: UpdateCameraLinkActionType = action;

        if (locationId) {
          updateLocation(locationId, (location) => {
            const targetCameraLink: CallhomeCameraLink | undefined = location.cameraLinks.find(
              (cl: CallhomeCameraLink) => cl.id === cameraLinkId,
            );
            if (targetCameraLink) {
              set(targetCameraLink, [prop], value);
            }
          });
        }

        LocationStore.emitChange();

        break;
      }

      case constants.INSERT_CAMERA_LINK: {
        const { locationId, newCameraLink }: InsertCameraLinkActionType = action;

        if (locationId) {
          updateLocation(locationId, (location) => {
            if (!location.cameraLinks) {
              location.cameraLinks = [];
            }
            location.cameraLinks.push(newCameraLink);
          });
        }

        LocationStore.emitChange();

        break;
      }

      case constants.DELETE_CAMERA_LINK: {
        const { locationId, cameraLinkId } = action;

        if (locationId) {
          updateLocation(locationId, (location) => {
            const targetIndex = location.cameraLinks.findIndex((cl) => cl.id === cameraLinkId);
            location.cameraLinks.splice(targetIndex, 1);
          });
        }

        LocationStore.emitChange();

        break;
      }

      case constants.UPDATE_CAMERA_MOTION_ALARM_DATA: {
        const updatedCamera = action?.camera;
        const cameraId = updatedCamera?.id;
        const locationId = updatedCamera?.siteId;

        if (!updatedCamera || !cameraId || !locationId) {
          console.warn('Unable to process updated camera in LocationStore: ', updatedCamera);
          break;
        }

        updateLocation(locationId, (location) => {
          const targetCamera = location.cameras.find((cam) => cam.id === cameraId);
          if (targetCamera) {
            targetCamera.isMotionAlarmEnabled = updatedCamera.isMotionAlarmEnabled;
            targetCamera.motionAlarmScheduleId = updatedCamera.motionAlarmScheduleId;
            if (updatedCamera.motionAlarmScheduleId) {
              targetCamera.motionAlarmSchedule = updatedCamera.motionAlarmSchedule;
            } else {
              delete targetCamera.motionAlarmSchedule;
            }
            if (updatedCamera.alarms) {
              targetCamera.alarms = updatedCamera.alarms;
            }
          } else {
            console.warn(`Could not find targetCamera ${cameraId} for location ${location.id}`);
          }
        });

        break;
      }

      case constants.UPDATE_LOCATION_MOTION_ALARM_SCHEDULE: {
        const { camera: updatedCamera, locationId, schedule } = action;
        const { id: scheduleId } = schedule;

        updateLocation(locationId, (location) => {
          location.motionAlarmScheduleId = scheduleId;

          if (scheduleId) {
            location.motionAlarmSchedule = cloneDeep(schedule);
          } else {
            delete location.motionAlarmSchedule;
          }

          if (updatedCamera) {
            const targetCamera = location.cameras.find((cam) => cam.id === updatedCamera.id);
            if (targetCamera) {
              targetCamera.isMotionAlarmEnabled = updatedCamera.isMotionAlarmEnabled;
              targetCamera.motionAlarmScheduleId = updatedCamera.motionAlarmScheduleId;
              if (updatedCamera.motionAlarmScheduleId) {
                targetCamera.motionAlarmSchedule = updatedCamera.motionAlarmSchedule;
              } else {
                delete targetCamera.motionAlarmSchedule;
              }
            }
          }
        });
        break;
      }

      // For a single location, update the latest motion alarm schedule information, including the
      // information inside the cameras property.
      case constants.GET_LATEST_MOTION_SCHEDULE_INFO: {
        const latestLocation = action.location;
        const latestLocationId = latestLocation.id;
        const latestLocationMotionAlarmSchedule = latestLocation.motionAlarmSchedule;
        const latestLocationMotionAlarmScheduleId = latestLocation.motionAlarmScheduleId;
        const latestLocationAlarmConfig = latestLocation.alarmConfig;
        const latestLocationAlarmContacts = latestLocation.alarmContacts;
        const latestLocationCameras = latestLocation.cameras ?? [];

        updateLocation(latestLocationId, (targetLocation) => {
          targetLocation.motionAlarmScheduleId = latestLocationMotionAlarmScheduleId;
          targetLocation.alarmConfig = latestLocationAlarmConfig;
          targetLocation.alarmContacts = latestLocationAlarmContacts;

          if (latestLocationMotionAlarmScheduleId) {
            targetLocation.motionAlarmSchedule = latestLocationMotionAlarmSchedule;
          } else {
            delete targetLocation.motionAlarmSchedule;
          }

          // Update `targetLocation.cameras` with latest `motionAlarmSchedule` and `motionAlarmScheduleId`
          // from each corresponding `latestLocationCameras`
          const latestLocationCamerasDict = keyBy(latestLocationCameras, 'id');

          targetLocation.cameras.forEach((targetCamera) => {
            const latestCamera = latestLocationCamerasDict[targetCamera.id];
            if (latestCamera) {
              targetCamera.isMotionAlarmEnabled = latestCamera.isMotionAlarmEnabled;
              targetCamera.motionAlarmScheduleId = latestCamera.motionAlarmScheduleId;
              if (latestCamera.motionAlarmScheduleId) {
                targetCamera.motionAlarmSchedule = latestCamera.motionAlarmSchedule;
              } else {
                delete targetCamera.motionAlarmSchedule;
              }
            }
          });
        });
        break;
      }

      case constants.UPDATE_LOCATION_ALARM_CONFIG: {
        const { locationId, alarmConfig } = action;

        updateLocation(locationId, (location) => {
          location.alarmConfig = alarmConfig;
        });
        break;
      }

      case constants.CREATE_LOCATION_ALARM_CONTACT: {
        const { locationId, alarmContact } = action;

        updateLocation(locationId, (location) => {
          location.alarmContacts.push(alarmContact);
        });
        break;
      }

      case constants.UPDATE_LOCATION_ALARM_CONTACT: {
        const { locationId, alarmContact } = action;

        updateLocation(locationId, (location) => {
          const indexToUpdate = findIndex(location.alarmContacts, ['id', alarmContact.id]);
          if (indexToUpdate > -1) {
            location.alarmContacts[indexToUpdate] = alarmContact;
          }
        });
        break;
      }

      case constants.REMOVE_LOCATION_ALARM_CONTACT: {
        const { locationId, alarmContact } = action;

        updateLocation(locationId, (location) => {
          location.alarmContacts = location.alarmContacts.filter(
            (contact) => contact.id !== alarmContact.id,
          );
        });
        break;
      }

      case constants.UPDATE_LOCATIONS_TAGS: {
        const { locations } = action;
        locations.forEach((locationWithTags: any) => {
          updateLocation(locationWithTags.id, (location) => {
            location.locationTags = locationWithTags.locationTags;
          });
        });
        break;
      }

      case constants.UPDATE_LOCATION_TAGS: {
        const { location } = action;
        updateLocation(location.id, (targetLocation) => {
          targetLocation.locationTags = location.locationTags;
        });
        break;
      }

      case constants.UPDATE_DEVICE_TAGS: {
        const { siteId, deviceId, tags } = action;
        updateLocation(siteId, (targetLocation) => {
          const device = targetLocation.devices?.find((d) => d.id === deviceId);
          if (device) device.tags = tags;
        });
        break;
      }

      case constants.UPDATE_DEVICES: {
        const { siteId, devices } = action;
        updateLocation(siteId, (targetLocation) => {
          targetLocation.devices = devices;
        });
        break;
      }

      case constants.UPDATE_CLOUD_CAMERA_RETENTION: {
        const { locations } = action;
        locations.forEach((locationWithTags: any) => {
          updateLocation(locationWithTags.id, (location) => {
            location.cameras = locationWithTags.cameras;
          });
        });
        break;
      }

      case constants.UPDATE_LOCATION_REP_CAMERA_GROUPS: {
        AppDispatcher.waitFor([CameraStore.dispatchToken]);
        const locationRepresentCameraGroups = action.payload;
        locationRepresentCameraGroups.forEach(
          ({ floorPlans, locationId }: { floorPlans: CallhomeFloorplan[]; locationId: string }) => {
            // for v1, we are not supporting multiple floorplans so we are just getting the 0 index to start
            const [floorplan] = floorPlans;
            _floorplanByLocationId[locationId] = floorplan;
          },
        );
        break;
      }

      case constants.CREATE_CAMERA_GROUP:
      case constants.UPDATE_CAMERA_GROUP: {
        AppDispatcher.waitFor([CameraStore.dispatchToken]);
        const cameraGroup = action.payload;
        if (cameraGroup.type === constants.CAMERA_GROUP_TYPE_FLOORPLAN) {
          _floorplanByLocationId[cameraGroup.locationId] = cameraGroup.floorPlans[0];
        }
        break;
      }

      case constants.UPDATE_MULTI_SITE_DEVICES_FEATURES: {
        const {
          sites,
        }: {
          sites: {
            [siteId: SiteId]: {
              devices: Array<Pick<DeviceEntity, 'id' | 'features'>>;
            };
          };
        } = action;

        _locations = _locations.map((location) => {
          const payloadDevices = sites[location.id]?.devices;
          if (!payloadDevices || !location?.devices) {
            return location;
          }
          const payloadById = keyBy(payloadDevices, (d) => d.id);
          const mergedDevices = location.devices.map((existingDevice) => {
            const updated = payloadById[existingDevice.id];
            if (!updated?.features) {
              return existingDevice;
            }
            return {
              ...existingDevice,
              features: merge({}, existingDevice.features ?? {}, updated.features ?? {}),
            };
          });
          return { ...location, devices: mergedDevices };
        });

        _locationsById = keyBy(_locations, (location) => location.id);

        LocationStore.emitChange();

        break;
      }
    }
  }),
};

function getCamerasWithLocation(location: VideoDiscoverySite) {
  const locationWithoutCameras = omit(location, ['cameras']);
  return location.cameras.map((camera) => {
    // Copy the `locationWithoutCameras` to the VideoDiscoveryLocation, because
    // there are places in the non-typescript code that expect it to be there.
    // Once everything is typescript, we can probably remove this.
    (camera as any).location = locationWithoutCameras;

    const cameraWithLocation: CamerasDictionaryCamera = {
      ...camera,
      location: locationWithoutCameras,
    };
    return cameraWithLocation;
  });
}

// only call this function when you are updating the camera dictionary for one location
// clone can be expensive if we are updating for all locations
function updateAllLocationCamerasDict(location: VideoDiscoverySite) {
  _allLocationCamerasDict = clone(_allLocationCamerasDict);
  const cameras = getCamerasWithLocation(location);
  for (const camera of cameras) {
    const { id } = camera;
    _allLocationCamerasDict[id] = camera;
  }
}

const processLocationList = async (user: UserSession, locations: VideoDiscoverySite[]) => {
  const lastClientIP = StorageAPI.getLocally(constants.LAST_CLIENT_IP);

  //
  // Sort locations alphabetically by the override name or name
  // If an overrideName exists use it to sort, otherwise fallback to the name
  //
  locations.sort((a, b) => {
    const nameA = a.name.toUpperCase();
    const nameB = b.name.toUpperCase();
    return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
  });

  _locations = locations;

  // Clear the allLocationCamerasDict
  // This will be updated afterwards when we iterate through each location
  // If we don't clear them, then there may be cameras from locations the user no longer has access to
  _allLocationCamerasDict = {};

  _deviceTagIds = new Set<string>();

  //
  // if client IP has changed, invalidate the stored local device ID
  //
  if (lastClientIP !== currentIP) {
    console.info(
      `Client IP has changed: [${lastClientIP}] -> [${currentIP}], invalidating local device id`,
    );
    StorageAPI.storeLocally(constants.LAST_CLIENT_IP, currentIP);
  }

  const canPlayFromRemote = !userIsLocalPlaybackOnly(user);
  if (!canPlayFromRemote) {
    // Don't bother checking devices for local access, as we'll only fetch locally anyways.
  }

  // TODO: Why do we go localOnly if `localToVPN` is true?  This means only 50%
  // of locations can be accessed locally, so we're possibly excluding half of
  // all locations.
  const forceRelay = StorageAPI.getLocally(constants.USE_VIDEO_SERVER_RELAY);
  const impersonated = AuthStore.isImpersonating();

  console.group('BASIC ACCESSIBILITY TEST');
  console.info('FORCE RELAY: ', forceRelay);
  console.info('IMPERSONATING: ', impersonated);

  _locations.forEach((l) => {
    // Cached location has lastUpdateTime.
    // It shouldn't be updated as the cached location isn't updated.
    if (!l.lastUpdateTime) {
      l.lastUpdateTime = new Date();
    }

    if (l.cameras) {
      // Update camera object to have the location data.
      // To avoid discrepancy between the location data and the location in the camera object,
      // all extra information that needed to be added to the location need to happen before this point.
      const cameras = getCamerasWithLocation(l);
      for (const camera of cameras) {
        const { id, name, overrideName } = camera;
        // process camera names, the overrideName has a higher priority than the name
        camera.name = overrideName || name;
        // process camera status
        camera.status = CameraUtil.cameraStatus(camera);
        // update the dictionary
        _allLocationCamerasDict[id] = camera;
      }

      // This needs to happen after we update the camera name with the overrideName
      l.cameras.sort(sortNameAlphabetically);
    }

    if (l.tags) {
      l.tags.forEach((tag) => _deviceTagIds?.add(tag));
    }
  });

  // add the `all-locations` location to the top
  _locations.unshift(generateAllLocations() as VideoDiscoverySite);

  _locationsById = keyBy(_locations, (location) => location.id);

  console.groupEnd();
};

export function forceDeviceLocal(user: UserSession): boolean {
  const forceRelay = StorageAPI.getLocally(constants.USE_VIDEO_SERVER_RELAY);
  const canPlayFromRemote = !userIsLocalPlaybackOnly(user);
  // Don't force local if the forceRelay preference is enabled. Relay must also be enabled.
  // This user preference is only available internally and it is only saved in local storage.
  if (forceRelay) {
    return false;
  }
  // Force local if the user is a local playback user or it's kiosk mode
  return !canPlayFromRemote || isKiosk();
}
